<script setup>

import { computed, onMounted, reactive, ref } from 'vue';

import '@campact/camper/core/CmprButton';
import FriendlyCaptcha from '@cta/frontend/components/FriendlyCaptcha.vue';
import csrfToken from '@cta/shared/csrf';
import browserService from '@cta/frontend/services/BrowserService';
import trackingService from '@cta/frontend/services/TrackingService';

import { debounce } from 'lodash/function';
import CmprInput from '@cta/frontend/components/CmprInput.vue';
import CmprSelect from '@cta/frontend/components/CmprSelect.vue';
import CmprCheckbox from '@cta/frontend/components/CmprCheckbox.vue';

const props = defineProps({
  additionalInformationEnabled: {
    type: Boolean,
    default: false,
  },
  additionalInformationLabel: {
    type: String,
    default: undefined,
  },
  additionalInformationDescription: {
    type: String,
    default: undefined,
  },
  autoSubmit: undefined,
  countryCodes: undefined,
  defaultCountryCode: undefined,
  formAnalyticsData: undefined,
  friendlyCaptchaSettings: {
    type: Object,
    default() {
      return {
        apiEndpoint: undefined,
        showCaptcha: false,
        simulateCaptchaMode: 'off',
        sitekey: undefined,
      };
    },
  },
  i18n: {
    type: Object,
    default: undefined,
  },
  contactPhoneNumberInputEnabled: {
    type: Boolean,
    default: false,
  },
  contactPhoneNumberInputDescription: {
    type: String,
    default: undefined,
  },
  contactPhoneNumberInputPlaceholder: {
    type: String,
    default: undefined,
  },
  contactPhoneNumberInputPrefill: undefined,
  prefilledAttributes: {
    type: Object,
    default() {
      return {
        firstname: undefined,
        lastname: undefined,
        countrycode: undefined,
        postalcode: undefined,
        region: undefined,
        emailaddress: undefined,
      };
    },
  },
  prefilledEmail: undefined,
  prefilledAny: undefined,
  prefilledComplete: undefined,
  regions: undefined,
  signPath: undefined,
  utmParameters: {
    type: Object,
    default: undefined,
  },
  validatePath: undefined,
});

const errors = reactive({
  emailaddress: undefined,
  firstname: undefined,
  lastname: undefined,
  contactPhoneNumber: undefined,
  postalcode: undefined,
  region: undefined,
});

const values = reactive({
  additionalInformation: false,
  countrycode: props.prefilledAttributes.countrycode || props.defaultCountryCode,
  emailaddress: props.prefilledAttributes.emailaddress,
  firstname: props.prefilledAttributes.firstname,
  contactPhoneNumber: props.contactPhoneNumberInputPrefill,
  lastname: props.prefilledAttributes.lastname,
  postalcode: props.prefilledAttributes.postalcode,
  region: props.prefilledAttributes.region,
});

const formAttributes = [
  'firstname',
  'lastname',
  'countrycode',
  'postalcode',
  'region',
  'emailaddress',
  'contactPhoneNumber',
];

const signCallForm = ref(undefined);
const formValid = computed(() => formAttributes.every((name) => errors[name] === undefined));
const errorsUnlocked = ref(false);

const showPrefilled = ref(props.prefilledComplete);
const submitPending = ref(false);
var submitButtonActive = false;

const captchaError = ref(undefined);
const captchaSolution = ref(!props.friendlyCaptchaSettings.showCaptcha
  || props.friendlyCaptchaSettings.simulateCaptchaMode === 'auto'
  ? 'simulated-response'
  : undefined);
const captchaValid = computed(() => captchaError.value === undefined);

function captchaComplete() {
  captchaError.value = undefined;

  if (!submitButtonActive) {
    trySubmit();
  }
}

function createFormData() {
  const formData = new FormData();
  formData.append('sign_form[firstname]', values.firstname || '');
  formData.append('sign_form[lastname]', values.lastname || '');
  formData.append('sign_form[countrycode]', values.countrycode || '');
  formData.append('sign_form[postalcode]', values.postalcode || '');
  formData.append('sign_form[region]', values.region || '');
  formData.append('sign_form[emailaddress]', values.emailaddress || '');
  formData.append('sign_form[additional_information]', values.additionalInformation);
  formData.append('sign_form[contact_phone_number]', values.contactPhoneNumber || '');
  formData.append('sign_form[captcha_solution]', captchaSolution.value || '');
  if (props.autoSubmit) {
    formData.append('sign_form[signed_directly]', true);
  }

  return formData;
}

async function fetchValidation(path) {
  const response = await fetch(path, {
    method: 'post',
    body: createFormData(),
    headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken() },
  });

  const json = await response.json();

  if (response.ok) {
    return { ok: response?.ok, messages: new Map(Object.entries(json)) };
  }

  browserService.reload();
}

async function validate(force = false) {
  if (!force && !errorsUnlocked.value) {
    return;
  }

  const { ok, messages } = await fetchValidation(props.validatePath);
  if (ok) {
    errors.emailaddress = messages.get('emailaddress');
    errors.firstname = messages.get('firstname');
    errors.lastname = messages.get('lastname');
    errors.contactPhoneNumber = messages.get('contact_phone_number');
    errors.postalcode = messages.get('postalcode');
    errors.region = messages.get('region');
  } else {
    browserService.reload();
  }
}

async function submit(path) {
  const formData = createFormData();

  Object.entries(props.utmParameters).forEach(([key, value]) => {
    if (value) formData.append(key, value);
  });

  const response = await fetch(path, {
    method: 'post',
    body: formData,
    headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken() },
  });

  if (response.ok) {
    const json = await response.json();
    const messages = new Map(Object.entries(json));
    captchaError.value = messages.get('captcha_solution');

    if (captchaValid.value) {
      trackingService.dispatchSendForm(signCallForm.value, {
        context: props.formAnalyticsData.context,
        value: props.formAnalyticsData.value,
      });
      browserService.redirectDelayed(json.redirectUrl);
    }
  } else {
    browserService.reload();
  }
}

async function submitClicked() {
  submitButtonActive = true;
  submitPending.value = true;
  errorsUnlocked.value = true;

  await validate();

  trySubmit();
  submitButtonActive = false;
}

async function trySubmit() {
  if (captchaSolution.value) {
    if (formValid.value && submitPending.value) {
      await submit(props.signPath);
    }

    setTimeout(() => {
      submitPending.value = false;
    }, 1000);
  }
}

const throttledValidate = debounce(async () => validate(), 500, { leading: true });
const throttledSubmit = debounce(async () => submitClicked(), 500, { leading: true });

onMounted(async () => {
  if (props.autoSubmit || props.prefilledAny) {
    await validate(true);

    if (!formValid.value) {
      showPrefilled.value = false;
    } else if (props.autoSubmit) {
      await submit(props.signPath);
    }
  }
});

const summaryPostalCode = computed(() => props.prefilledAttributes.countrycode == 'RO'
  ? props.prefilledAttributes.region
  : props.prefilledAttributes.postalcode);
</script>

<template>
  <form
    ref="signCallForm"
    class="mt-50"
    data-analytics-id="sign-call-form"
  >
    <div class="form__grid">
      <template v-if="!showPrefilled">
        <div class="form__grid-column--variable">
          <CmprInput
            v-model="values.firstname"
            autocomplete="given-name"
            :class="{'is-invalid': !!errors.firstname}"
            :data-analytics-context="formAnalyticsData.context"
            data-analytics-id="sign-call-form-firstname-field"
            data-analytics-inner-text="firstname"
            :placeholder="i18n.attributes.firstname"
            @input="throttledValidate"
            @keydown.enter="throttledSubmit"
          />
          <cmpr-form-hint
            v-if="!!errors.firstname"
            variant="error"
          >
            {{ errors.firstname }}
          </cmpr-form-hint>
        </div>
        <div class="form__grid-column--variable">
          <CmprInput
            v-model="values.lastname"
            autocomplete="family-name"
            :class="{'is-invalid': !!errors.lastname}"
            :data-analytics-context="formAnalyticsData.context"
            data-analytics-id="sign-call-form-lastname-field"
            data-analytics-inner-text="lastname"
            :placeholder="i18n.attributes.lastname"
            @input="throttledValidate"
            @keydown.enter="throttledSubmit"
          />
          <cmpr-form-hint
            v-if="!!errors.lastname"
            variant="error"
          >
            {{ errors.lastname }}
          </cmpr-form-hint>
        </div>
        <div class="form__grid-column--variable">
          <CmprSelect
            v-model="values.countrycode"
            :aria-label="i18n.attributes.countrycode"
            autocomplete="country"
            :data-analytics-context="formAnalyticsData.context"
            data-analytics-id="sign-call-form-countrycode-field"
            data-analytics-inner-text="countrycode"
          >
            <option
              v-for="(code, name) in countryCodes"
              :key="code"
              :value="code"
            >
              {{ name }}
            </option>
          </CmprSelect>
        </div>
        <div
          v-if="values.countrycode !== 'RO'"
          class="form__grid-column--variable"
        >
          <CmprInput
            v-model="values.postalcode"
            autocomplete="postal-code"
            :class="{'is-invalid': !!errors.postalcode}"
            :data-analytics-context="formAnalyticsData.context"
            data-analytics-id="sign-call-form-postalcode-field"
            data-analytics-inner-text="postalcode"
            :placeholder="i18n.attributes.postalcode"
            @input="throttledValidate"
            @keydown.enter="throttledSubmit"
          />
          <cmpr-form-hint
            v-if="!!errors.postalcode"
            variant="error"
          >
            {{ errors.postalcode }}
          </cmpr-form-hint>
        </div>
        <div
          v-if="values.countrycode === 'RO'"
          class="form__grid-column--variable"
        >
          <CmprSelect
            v-model="values.region"
            :aria-label="i18n.attributes.region"
            autocomplete="address-level1"
            :class="{'is-invalid': !!errors.region}"
            :data-analytics-context="formAnalyticsData.context"
            data-analytics-id="sign-call-form-region-field"
            data-analytics-inner-text="region"
            @change="throttledValidate"
          >
            <option
              disabled="disabled"
              :value="undefined"
            >
              {{ i18n.selectPrompt }}
            </option>
            <option
              v-for="(name) in regions"
              :key="name"
              :value="name"
            >
              {{ name }}
            </option>
          </CmprSelect>

          <cmpr-form-hint
            v-if="!!errors.region"
            variant="error"
          >
            {{ errors.region }}
          </cmpr-form-hint>
        </div>
        <div class="form__grid-column">
          <CmprInput
            v-model="values.emailaddress"
            autocomplete="email"
            :class="{'is-invalid': !!errors.emailaddress}"
            :data-analytics-context="formAnalyticsData.context"
            data-analytics-id="sign-call-form-emailaddress-field"
            data-analytics-inner-text="emailaddress"
            :placeholder="i18n.attributes.emailaddress"
            :readonly="prefilledEmail"
            @input="throttledValidate"
            @keydown.enter="throttledSubmit"
          />
          <cmpr-form-hint
            v-if="!!errors.emailaddress"
            variant="error"
          >
            {{ errors.emailaddress }}
          </cmpr-form-hint>
        </div>
      </template>
      <template v-if="showPrefilled">
        <cmpr-prefilled-area
          class="form__prefilled"
          editable="editable"
          variant="success"
          @click.prevent="showPrefilled = false"
        >
          <strong>
            {{ prefilledAttributes.firstname }}
            {{ prefilledAttributes.lastname }}
          </strong>
          <br>
          <span>
            {{ `${prefilledAttributes.countrycode} · ${summaryPostalCode}` }}
          </span>
          <br>
          <span>
            {{ prefilledAttributes.emailaddress }}
          </span>
        </cmpr-prefilled-area>
      </template>
    </div>
    <div
      v-if="contactPhoneNumberInputEnabled"
      class="mt-60"
    >
      <!-- eslint-disable vue/no-v-html -->
      <div
        v-if="contactPhoneNumberInputDescription"
        class="sign-call_form__text-muted mb-40"
        v-html="contactPhoneNumberInputDescription"
      />
      <!-- eslint-enable vue/no-v-html -->
      <CmprInput
        id="contact-phone-number-input"
        v-model="values.contactPhoneNumber"
        autocomplete="mobile"
        :class="{ 'is-invalid': !!errors.contactPhoneNumber }"
        :data-analytics-context="formAnalyticsData.context"
        data-analytics-id="sign-call-form-contact-phone-number-field"
        data-analytics-inner-text="contact-phone-number"
        :placeholder="contactPhoneNumberInputPlaceholder"
        type="tel"
        @input="throttledValidate"
        @keydown.enter="throttledSubmit"
      />
      <cmpr-form-hint
        v-if="!!errors.contactPhoneNumber"
        variant="error"
      >
        {{ errors.contactPhoneNumber }}
      </cmpr-form-hint>
    </div>
    <div
      v-if="additionalInformationEnabled"
      class="mt-80"
    >
      <!-- eslint-disable vue/no-v-html -->
      <div
        v-if="additionalInformationDescription"
        class="sign-call_form__text-muted mb-40"
        v-html="additionalInformationDescription"
      />
      <!-- eslint-enable vue/no-v-html -->
      <div class="cmpr-checkbox-control">
        <CmprCheckbox
          id="additional-info-checkbox"
          v-model="values.additionalInformation"
          :data-analytics-context="formAnalyticsData.context"
          data-analytics-id="sign-call-form-additional-info-checkbox"
          data-analytics-inner-text="additional-information"
        />
        <label
          class="cmpr-checkbox-control__label"
          for="additional-info-checkbox"
        > {{ additionalInformationLabel }}
        </label>
      </div>
    </div>

    <FriendlyCaptcha
      v-model:solution="captchaSolution"
      :error="captchaError"
      :error-unlocked="!captchaValid"
      :settings="friendlyCaptchaSettings"
      @complete="captchaComplete"
    >
      <div class="mt-80">
        <cmpr-button
          class="w-100 sign-form__submit-button"
          data-analytics-context="sign-form"
          data-analytics-id="sign-call-form-submit-button"
          :disabled="submitPending"
          type="button"
          @click="throttledSubmit"
        >
          {{ i18n.signButtonText }}
        </cmpr-button>
      </div>
    </FriendlyCaptcha>
  </form>
</template>

<style scoped lang="scss">
@import "@cta/frontend/stylesheets/custom-media";

form {
  --form-grid-column-variable-grid-column: span 1;

  @media (--cmpr-breakpoint-desktop-min) {
    --form-grid-column-variable-grid-column: span 2;
  }

  @media (--cmpr-breakpoint-xxl-min) {
    --form-grid-column-variable-grid-column: span 1;
  }
}

.form__grid {
  display: grid;
  gap: var(--cmpr-space-60);
  grid-template-columns: repeat(2, 1fr);
}

.form__grid-column {
  grid-column: span 2;

  &--variable {
    grid-column: var(--form-grid-column-variable-grid-column);
  }
}

.form__prefilled {
  grid-column: span 4;
  max-width: 100%;
  overflow: hidden;
}

.sign-form__submit-button {
  --cmpr-button-padding-inline: unset;
}
</style>
